"use client";

import { useNavigationTreeQueryParams } from "@faire/retailer-visitor-shared/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/Fetching/useNavigationTreeQueryParams";
import { useSDUIFilteredComponentsRequest } from "@faire/retailer-visitor-shared/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/Fetching/useSDUIFilteredComponentsRequest";
import { useSDUIFiltersRequest } from "@faire/retailer-visitor-shared/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/Fetching/useSDUIFiltersRequest";
import { useSettingMigrateCatnavToV3LayoutApis } from "@faire/retailer-visitor-shared/settings/useSettingMigrateCatnavToV3LayoutApis";
import { QueryParameters as FilteredComponentsQueryParams } from "@faire/web-api--source/endpoints/www/api/v2/layout/filtered-components/post";
import { useQuery as useFilteredComponentsQuery } from "@faire/web-api--source/endpoints/www/api/v2/layout/filtered-components/post-hooks";
import { useQuery_escape_hatch as useFiltersQuery } from "@faire/web-api--source/endpoints/www/api/v2/layout/filters/post-hooks";
import { useQuery as useNavigationTreeQuery } from "@faire/web-api--source/endpoints/www/api/v2/layout/navigation-tree/get-hooks";
import { useQuery as useFilteredComponentsQueryV3 } from "@faire/web-api--source/endpoints/www/api/v3/layout/filtered-components/post-hooks";
import { useQuery_escape_hatch as useFiltersQueryV3 } from "@faire/web-api--source/endpoints/www/api/v3/layout/filters/post-hooks";
import { IFilterablePaginatedSection } from "@faire/web-api--source/indigofair/data/IFilterablePaginatedSection";
import { keepPreviousData } from "@tanstack/react-query";

export const useFilterablePaginatedSectionQuery = (
  filterablePaginatedSection: IFilterablePaginatedSection,
  disableQueries?: boolean
) => {
  const SDUIFiltersRequest = useSDUIFiltersRequest(filterablePaginatedSection);
  const shouldUseCatnavV3LayoutApis = useSettingMigrateCatnavToV3LayoutApis();

  const { isFetching: isV2FilterLoading, data: v2FiltersResponse } =
    useFiltersQuery(SDUIFiltersRequest, {
      staleTime: Infinity,
      placeholderData: keepPreviousData,
      enabled: !disableQueries && !shouldUseCatnavV3LayoutApis,
    });

  const { isFetching: isV3FilterLoading, data: v3FiltersResponse } =
    useFiltersQueryV3(SDUIFiltersRequest, {
      staleTime: Infinity,
      placeholderData: keepPreviousData,
      enabled: !disableQueries && shouldUseCatnavV3LayoutApis,
    });

  const SDUIFilteredComponentsRequest = useSDUIFilteredComponentsRequest(
    filterablePaginatedSection
  );
  const {
    isFetching: isV2FilteredComponentsFetching,
    isError: isV2FilteredComponentsError,
    isLoading: isV2FilteredComponentsLoading,
    data: v2FilteredComponentsData,
    dataUpdatedAt: v2FilteredComponentsDataUpdatedAt,
  } = useFilteredComponentsQuery(
    SDUIFilteredComponentsRequest,
    FilteredComponentsQueryParams.build({}),
    {
      staleTime: Infinity,
      placeholderData: keepPreviousData,
      enabled: !disableQueries && !shouldUseCatnavV3LayoutApis,
    }
  );

  const {
    isFetching: isV3FilteredComponentsFetching,
    isError: isV3FilteredComponentsError,
    isLoading: isV3FilteredComponentsLoading,
    data: v3FilteredComponentsData,
    dataUpdatedAt: v3FilteredComponentsDataUpdatedAt,
  } = useFilteredComponentsQueryV3(SDUIFilteredComponentsRequest, {
    staleTime: Infinity,
    placeholderData: keepPreviousData,
    enabled: !disableQueries && shouldUseCatnavV3LayoutApis,
  });

  const navigationTreeQueryParams = useNavigationTreeQueryParams(
    filterablePaginatedSection
  );
  const { data: navigationTreeData, isFetching: isNavigationTreeLoading } =
    useNavigationTreeQuery(navigationTreeQueryParams, {
      staleTime: Infinity,
      placeholderData: keepPreviousData,
      enabled: !disableQueries,
    });

  return {
    isFilterLoading: shouldUseCatnavV3LayoutApis
      ? isV3FilterLoading
      : isV2FilterLoading,
    isFilteredComponentsFetching: shouldUseCatnavV3LayoutApis
      ? isV3FilteredComponentsFetching
      : isV2FilteredComponentsFetching,
    isFilteredComponentsError: shouldUseCatnavV3LayoutApis
      ? isV3FilteredComponentsError
      : isV2FilteredComponentsError,
    isFilteredComponentsLoading: shouldUseCatnavV3LayoutApis
      ? isV3FilteredComponentsLoading
      : isV2FilteredComponentsLoading,
    isNavigationTreeLoading,
    filtersResponse: shouldUseCatnavV3LayoutApis
      ? v3FiltersResponse
      : v2FiltersResponse,
    filteredComponentsData: shouldUseCatnavV3LayoutApis
      ? v3FilteredComponentsData
      : v2FilteredComponentsData,
    filteredComponentsDataUpdatedAt: shouldUseCatnavV3LayoutApis
      ? v3FilteredComponentsDataUpdatedAt
      : v2FilteredComponentsDataUpdatedAt,
    navigationTreeData,
  };
};
